/**
 * Transport client for the wizard endpoint(s).
 *
 * Hits `POST /api/debate/wizard/personas` (real LLM, no mocks). The route is
 * proxied via vite/nginx to the orchestrator at 127.0.0.1:8088.
 */
import type { AgentRole, DebateTopology, SamplingOverrides } from "../domain/types";
import type { WizardPersona } from "../domain/wizard";
import { withCsrfHeader } from "./csrf";

const WIZARD_PERSONAS_ENDPOINT = "/api/debate/wizard/personas";
const WIZARD_PERSONA_FROM_TITLE_ENDPOINT = "/api/debate/wizard/persona/from-title";
const WIZARD_PERSONA_PATCH_ENDPOINT = "/api/debate/wizard/personas";
const WIZARD_CANDIDATES_ENDPOINT = "/api/debate/wizard/council-candidates";

export interface WizardPersonasRequest {
  query: string;
  topology: DebateTopology;
  agent_count: number;
  seed?: number | null;
  /**
   * Pending debate session UUID — when present, the backend pulls
   * pool-scoped RAG snippets from this session and injects them into
   * the persona-design prompt. Lets the LLM produce DOMAIN-AWARE
   * personas for project-specific questions ("UKKS değerlendirmesi"
   * → produces UKKS doktrin uzmanı, sistem entegratörü, vb.).
   */
  session_id?: string | null;
}

export interface WizardPersonasResponse {
  personas: WizardPersona[];
  seatOrder: string[];
}

export class WizardApiError extends Error {
  override readonly name = "WizardApiError";
  readonly status: number;
  readonly code: string | null;

  constructor(message: string, status: number, code: string | null = null) {
    super(message);
    this.status = status;
    this.code = code;
    Object.setPrototypeOf(this, WizardApiError.prototype);
  }
}

/**
 * `macdm/api/errors.py::_envelope` — FLAT, not nested under `error`.
 * Reading `payload.error.message` (as this file used to) silently discarded
 * every backend explanation, including the Turkish validation sentences, and
 * showed a bare status code instead.
 */
export interface ErrorEnvelope {
  ok?: boolean;
  code?: string;
  message?: string;
  details?: unknown;
  /** FastAPI's own default shape, still emitted by non-`ApiError` paths. */
  detail?: unknown;
}

/**
 * Gövdeyi okur; bozuk/boş JSON'da `null` döner.
 *
 * İPTAL HATASINI YUTMAZ. Eski sürüm her hatayı yutuyordu: zaman aşımı
 * gövde aktarımının ORTASINDA tetiklendiğinde `AbortError` sessizce
 * `null`'a dönüşüyor, çağıran da bunu "geçersiz yanıt" sanıyordu.
 */
async function readJsonSafe(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch (err) {
    if ((err as { name?: string } | null)?.name === "AbortError") throw err;
    return null;
  }
}

/** Backend sentence for the operator, or `null` when the body carries none. */
export function envelopeMessage(payload: ErrorEnvelope | null): string | null {
  if (!payload) return null;
  if (typeof payload.message === "string" && payload.message.trim()) {
    return payload.message;
  }
  if (typeof payload.detail === "string" && payload.detail.trim()) {
    return payload.detail;
  }
  return null;
}

export function envelopeCode(payload: ErrorEnvelope | null): string | null {
  return payload && typeof payload.code === "string" ? payload.code : null;
}

/* ---------------------------------------------------------------------- *
 * İstek zaman aşımı
 *
 * `fetch` VARSAYILAN OLARAK SÜRESİZDİR. Bu uçlar LLM'e bağlı olduğu için
 * yanıt hiç gelmediğinde sihirbaz "Üretiliyor…" spinner'ında SONSUZA
 * KADAR kalıyordu — operatör raporu (2026-08-02, 8 üyeli konsey): "10
 * dk dır bekliyor, süreç ilerlemiyor". Hata durumu hiç kurulmadığı için
 * ne mesaj ne de "Tekrar dene" düğmesi görünüyordu.
 *
 * Tavanlar arka ucun KENDİ son-tarihinin (`wizard_personas_deadline_s`,
 * 90 sn) ÜSTÜNDE tutulur: normal koşulda sunucunun açıklayıcı 502'si
 * kazanır, buradaki tavan yalnızca yanıtın hiç dönmediği (ağ/kenar
 * takılması) durum için son savunma hattıdır.
 * ---------------------------------------------------------------------- */

const COUNCIL_TIMEOUT_MS = 120_000;
const SINGLE_PERSONA_TIMEOUT_MS = 60_000;
const PATCH_TIMEOUT_MS = 20_000;

interface TimeoutGuard {
  signal: AbortSignal;
  /** Zaman aşımı mı tetikledi, yoksa çağıran mı iptal etti? */
  expired(): boolean;
  dispose(): void;
}

function startTimeout(ms: number, caller?: AbortSignal): TimeoutGuard {
  const controller = new AbortController();
  const deadline = Date.now() + ms;
  let expired = false;

  const fire = () => {
    if (expired || controller.signal.aborted) return;
    expired = true;
    controller.abort();
  };

  const timer = setTimeout(fire, ms);

  // Arka plan sekmesi koruması.
  //
  // Tarayıcılar arka plandaki sekmede `setTimeout`u kısıtlar (Chrome'da
  // dakikada bir'e kadar seyrekleşir, uzun süre sonra tamamen dondurur) ve
  // makine uykuya girerse zamanlayıcı hiç ilerlemez. Yükleme ekranındaki
  // sayaç ise `Date.now()` farkını okuduğu için DUVAR SAATİYLE ilerlemeye
  // devam eder. Bu asimetri, operatörün gördüğü tabloyu birebir üretiyor:
  // ekranda 128 sn yazıyor ama 120 sn'lik tavan hiç tetiklenmemiş oluyor.
  //
  // Bu yüzden son tarih, sekme yeniden görünür olduğunda duvar saatine
  // karşı da denetlenir — kullanıcı ekrana döndüğü anda ölü bekleme yerine
  // açık bir hata görür.
  const onVisible = () => {
    if (document.visibilityState === "visible" && Date.now() >= deadline) {
      fire();
    }
  };
  const hasDoc = typeof document !== "undefined";
  if (hasDoc) {
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
  }

  const relay = () => controller.abort();
  caller?.addEventListener("abort", relay);
  return {
    signal: controller.signal,
    expired: () => expired,
    dispose: () => {
      clearTimeout(timer);
      if (hasDoc) {
        document.removeEventListener("visibilitychange", onVisible);
        window.removeEventListener("focus", onVisible);
      }
      caller?.removeEventListener("abort", relay);
    },
  };
}

/**
 * İsteği zaman aşımıyla sarar ve **gövdeyi de aynı bütçenin içinde** okur.
 * Süre dolarsa çağırana ham `AbortError` yerine, sihirbazın hata panelinde
 * doğrudan gösterilebilecek Türkçe bir `WizardApiError` döner (HTTP 504 —
 * ağ geçidi zaman aşımı semantiği).
 *
 * NEDEN gövde de bütçenin içinde:
 * Önceki sürüm yalnızca `fetch()` çağrısını sarıyordu. `fetch`, başlıklar
 * geldiği anda çözülür; gövde ondan SONRA akar. VPN/uzak ağ üzerinde
 * başlıklar zamanında gelip gövde aktarımı takıldığında zaman aşımı çoktan
 * temizlenmiş oluyor ve `response.json()` SÜRESİZ bekliyordu. Operatör
 * raporu tam bu şekildeydi: 128 sn geçmesine rağmen 120 sn'lik tavan hiç
 * devreye girmiyor, sihirbaz "Üretiliyor…" durumunda kalıyordu. Gövde
 * nihayet tamamlandığında personalar geliyor — yani veri kaybı yok, ama
 * kullanıcı dakikalarca ölü bir ekrana bakıyordu.
 */
interface TimedJsonResult {
  response: Response;
  payload: unknown;
}

async function requestJson(
  endpoint: string,
  init: RequestInit,
  {
    timeoutMs,
    signal,
    label,
  }: { timeoutMs: number; signal: AbortSignal | undefined; label: string },
): Promise<TimedJsonResult> {
  const guard = startTimeout(timeoutMs, signal);
  try {
    const response = await fetch(endpoint, { ...init, signal: guard.signal });
    // Gövde okuması da guard içinde: sinyal iptal edilirse akış da iptal
    // olur, böylece yarım kalan aktarım sonsuza kadar bekletemez.
    const payload = await readJsonSafe(response);
    return { response, payload };
  } catch (err) {
    if (guard.expired()) {
      throw new WizardApiError(
        `${label} ${Math.round(timeoutMs / 1000)} saniye içinde yanıt vermedi. ` +
          "Model sağlayıcısı yoğun olabilir veya ağ bağlantınız (VPN/uzak erişim) " +
          "yanıtı yarıda kesmiş olabilir — konsey boyutunu küçültüp tekrar deneyin.",
        504,
        "wizard.timeout",
      );
    }
    throw err;
  } finally {
    guard.dispose();
  }
}

export async function fetchWizardPersonas(
  body: WizardPersonasRequest,
  options: { signal?: AbortSignal; endpoint?: string } = {},
): Promise<WizardPersonasResponse> {
  const endpoint = options.endpoint ?? WIZARD_PERSONAS_ENDPOINT;
  const init: RequestInit = {
    method: "POST",
    headers: withCsrfHeader({
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
    body: JSON.stringify({
      query: body.query,
      topology: body.topology,
      agent_count: body.agent_count,
      ...(body.seed !== undefined && body.seed !== null
        ? { seed: body.seed }
        : {}),
      ...(body.session_id ? { session_id: body.session_id } : {}),
    }),
    cache: "no-store",
    credentials: "include",
  };
  const { response, payload: raw } = await requestJson(endpoint, init, {
    timeoutMs: COUNCIL_TIMEOUT_MS,
    signal: options.signal,
    label: "Konsey üretici",
  });

  if (!response.ok) {
    const envelope = raw as ErrorEnvelope | null;
    const code = envelopeCode(envelope);
    const message =
      envelopeMessage(envelope) ??
      `Konsey üretici yanıt vermedi (HTTP ${response.status} ${response.statusText})`;
    throw new WizardApiError(message, response.status, code);
  }

  const payload = raw as WizardPersonasResponse | null;
  if (!payload || !Array.isArray(payload.personas)) {
    throw new WizardApiError(
      "Konsey üretici geçersiz bir yanıt döndürdü.",
      response.status,
      "wizard.invalid_payload",
    );
  }
  return {
    personas: payload.personas,
    seatOrder: payload.seatOrder ?? payload.personas.map((p) => p.id),
  };
}

/* ====================================================================== *
 * /council-candidates — aday üye kataloğu.
 *
 * Operatör soruyu ve belgeleri okuyan bir aday listesi ister, kararı
 * kendi verir. Tam-otomatik üretim (tek kutba kayıyordu) ile elle unvan
 * yazma arasındaki eksik adım.
 * ====================================================================== */

export interface CouncilCandidate {
  id: string;
  title: string;
  category: string;
  /** Bu adayın BU SORUYA ne katacağı — seçim gerekçesi. */
  focus: string;
  role: AgentRole;
  /** Sistemin önerdiği çekirdek kadronun parçası mı. */
  recommended: boolean;
}

export interface CouncilCandidatesResponse {
  candidates: CouncilCandidate[];
  categories: string[];
}

export interface CouncilCandidatesRequest {
  query: string;
  count?: number;
  session_id?: string | null;
  /** Adım 2'deki Konsey Yönergesi — slate'i eğer. */
  directive?: string | null;
}

export async function fetchCouncilCandidates(
  body: CouncilCandidatesRequest,
  options: { signal?: AbortSignal; endpoint?: string } = {},
): Promise<CouncilCandidatesResponse> {
  const endpoint = options.endpoint ?? WIZARD_CANDIDATES_ENDPOINT;
  const init: RequestInit = {
    method: "POST",
    headers: withCsrfHeader({
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
    body: JSON.stringify({
      query: body.query,
      count: body.count ?? 24,
      ...(body.session_id ? { session_id: body.session_id } : {}),
      ...(body.directive && body.directive.trim()
        ? { directive: body.directive.trim() }
        : {}),
    }),
    cache: "no-store",
    credentials: "include",
  };
  const { response, payload: raw } = await requestJson(endpoint, init, {
    timeoutMs: COUNCIL_TIMEOUT_MS,
    signal: options.signal,
    label: "Aday üretici",
  });

  if (!response.ok) {
    const envelope = raw as ErrorEnvelope | null;
    const message =
      envelopeMessage(envelope) ??
      `Aday üretici yanıt vermedi (HTTP ${response.status} ${response.statusText})`;
    throw new WizardApiError(message, response.status, envelopeCode(envelope));
  }

  const payload = raw as CouncilCandidatesResponse | null;
  if (!payload || !Array.isArray(payload.candidates) || payload.candidates.length === 0) {
    throw new WizardApiError(
      "Aday üretici geçersiz bir yanıt döndürdü.",
      response.status,
      "wizard.candidates.invalid_payload",
    );
  }
  return {
    candidates: payload.candidates,
    categories:
      Array.isArray(payload.categories) && payload.categories.length > 0
        ? payload.categories
        : [...new Set(payload.candidates.map((c) => c.category))],
  };
}

/* ====================================================================== *
 * /persona/from-title — single-persona AI synthesis from a job title.
 * ====================================================================== */

export interface WizardPersonaFromTitleRequest {
  title: string;
  topic?: string | null;
  seat_index?: number;
  total_seats?: number;
  /**
   * Tahtadaki mevcut üye adları. Bu uç nokta koltuk başına ayrı çağrıldığı
   * ve her çağrı bağımsız olduğu için model aynı adı tekrar üretebiliyor;
   * dolu adları göndermek sunucunun çakışmayı çözmesini sağlar.
   */
  taken_names?: string[];
}

export async function fetchPersonaFromTitle(
  body: WizardPersonaFromTitleRequest,
  options: { signal?: AbortSignal; endpoint?: string } = {},
): Promise<WizardPersona> {
  const endpoint = options.endpoint ?? WIZARD_PERSONA_FROM_TITLE_ENDPOINT;
  const init: RequestInit = {
    method: "POST",
    headers: withCsrfHeader({
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
    body: JSON.stringify({
      title: body.title,
      topic: body.topic ?? null,
      seat_index: body.seat_index ?? 0,
      total_seats: body.total_seats ?? 5,
      taken_names: body.taken_names ?? [],
    }),
    cache: "no-store",
    credentials: "include",
  };
  const { response, payload: raw } = await requestJson(endpoint, init, {
    timeoutMs: SINGLE_PERSONA_TIMEOUT_MS,
    signal: options.signal,
    label: "Persona üretici",
  });

  if (!response.ok) {
    const envelope = raw as ErrorEnvelope | null;
    const code = envelopeCode(envelope);
    const message =
      envelopeMessage(envelope) ??
      `Persona üretici yanıt vermedi (HTTP ${response.status} ${response.statusText})`;
    throw new WizardApiError(message, response.status, code);
  }

  const payload = raw as WizardPersona | null;
  if (!payload || typeof payload.id !== "string") {
    throw new WizardApiError(
      "Persona üretici geçersiz bir yanıt döndürdü.",
      response.status,
      "wizard.persona_from_title.invalid_payload",
    );
  }
  return payload;
}

/* ====================================================================== *
 * PATCH /api/debate/wizard/personas/{id} — drawer edit persistence.
 *
 * The drawer mutates wizard-generated personas (model, sampling, voice,
 * system instruction). Without this PATCH the engine reads the original
 * row from `macdm.personas` when the operator clicks "Tartışmayı Başlat"
 * and the customisations evaporate. Calling this in `editPersona()`
 * persists them in the same DB row before the debate session begins.
 * ====================================================================== */

export interface WizardPersonaPatchBody {
  displayName?: string;
  expertise?: string;
  systemInstruction?: string;
  stanceSummary?: string;
  themeColor?: string;
  voiceRate?: string;
  voicePitch?: string;
  voiceVolume?: string;
  modelId?: string;
  samplingOverrides?: SamplingOverrides;
}

export async function patchWizardPersona(
  personaId: string,
  body: WizardPersonaPatchBody,
  options: { signal?: AbortSignal } = {},
): Promise<void> {
  const endpoint = `${WIZARD_PERSONA_PATCH_ENDPOINT}/${encodeURIComponent(personaId)}`;
  const init: RequestInit = {
    method: "PATCH",
    headers: withCsrfHeader({
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
    body: JSON.stringify(body),
    cache: "no-store",
    credentials: "include",
  };
  const { response, payload: raw } = await requestJson(endpoint, init, {
    timeoutMs: PATCH_TIMEOUT_MS,
    signal: options.signal,
    label: "Persona güncelleme",
  });
  if (!response.ok) {
    const envelope = raw as ErrorEnvelope | null;
    const code = envelopeCode(envelope);
    const message =
      envelopeMessage(envelope) ?? `Persona güncellenemedi (HTTP ${response.status})`;
    throw new WizardApiError(message, response.status, code);
  }
}
